import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listTradeFairs, TradeFairState, type TradeFair } from '@/api/tradeFairs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatPeriod } from '@/lib/dates'

/**
 * The fair a lead is captured at — required, and the one field somebody at a stand should never
 * have to think about.
 *
 * Hence [preselectRunning]: on a new lead the fair that is on **today** is preselected. The
 * backend computes that from the period in the business time zone, so the app does not need to
 * know any dates, or which side of midnight it is on.
 *
 * That preselection is reported through [onPreselect] rather than [onChange], because the two are
 * not the same event: one is the app filling a field in, the other is somebody making a choice.
 * The unsaved-changes guard has to be able to tell them apart, or closing a dialog nobody typed
 * into would ask whether to discard the app's own default.
 */
export function TradeFairSelect({
  id,
  value,
  onChange,
  onPreselect,
  preselectRunning,
  invalid,
  describedBy,
}: {
  id: string
  value: string | null
  onChange: (id: string) => void
  /** The fair that is running today, filled in by this component and not by the user. */
  onPreselect?: (id: string) => void
  /** True while creating: an existing lead keeps the fair it was written at. */
  preselectRunning: boolean
  /** The field is required, so it can be the one error on the form — and has to announce it. */
  invalid?: boolean
  describedBy?: string
}) {
  const { t } = useTranslation()
  const [fairs, setFairs] = useState<TradeFair[]>([])
  /**
   * The effect below runs once and never again, so the `value` it closed over stays whatever it
   * was at mount. Reading the current one through a ref is what keeps the preselection from
   * landing on top of a choice somebody made while the list was still loading.
   */
  const chosen = useRef(value)
  chosen.current = value

  useEffect(() => {
    const controller = new AbortController()
    // Newest first, and a hundred of them is more years than this will see.
    listTradeFairs({ size: 100 }, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) {
          return
        }
        setFairs(page.tradeFairs)
        if (preselectRunning && !chosen.current) {
          const running = page.tradeFairs.find((fair) => fair.state === TradeFairState.RUNNING)
          if (running) {
            ;(onPreselect ?? onChange)(running.id)
          }
        }
      })
      .catch(() => {
        // The select stays empty and the form's own validation says the fair is required; a toast
        // here would fire behind an open dialog for something the user can see for themselves.
      })
    return () => controller.abort()
    // Deliberately once per mount: re-running this would fight the user's own choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Undefined until the list has loaded, which puts Radix back on its own portal for that beat.
  const selected = fairs.find((fair) => fair.id === value)

  return (
    // `undefined`, never `''`. An empty string looks like a value to Radix and like no value to
    // its item collection, and it answers that contradiction by firing `onValueChange('')` right
    // back — which silently undid the preselection a moment after it happened. The guard on the
    // callback is the second half of the same lesson: a reset must not be able to clear a choice.
    <Select value={value ?? undefined} onValueChange={(next) => next && onChange(next)}>
      <SelectTrigger
        id={id}
        className="w-full"
        aria-invalid={invalid}
        aria-describedby={describedBy}
      >
        {/* Children of their own, so the trigger shows one line rather than the option's two.
            Radix drops the portal it would otherwise fill this span with as soon as it has
            children — which is what keeps a field the height of every other field on the form.
            The city and the period earn their place in the list, where they are what you choose
            by; once chosen, the name is the answer. */}
        <SelectValue placeholder={t('leadForm.tradeFairPlaceholder')}>
          {selected && (
            <span className="truncate">
              {selected.name}
              <span className="text-muted-foreground"> · {selected.city}</span>
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {fairs.map((fair) => (
          // `textValue` is not optional here: an item whose children are more than a string
          // leaves Radix with nothing to show in the trigger, and it renders the placeholder as
          // though nothing were selected at all.
          <SelectItem key={fair.id} value={fair.id} textValue={fair.name}>
            <span className="flex flex-col items-start gap-0.5">
              <span>
                {fair.name}
                {fair.state === TradeFairState.RUNNING && ` · ${t('tradeFairs.state.RUNNING')}`}
              </span>
              <span className="text-muted-foreground text-xs">
                {fair.city} · {formatPeriod(fair.startDate, fair.endDate)}
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
